import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Accessibility, Mail, Phone } from 'lucide-react';

const WebsiteAccessibilityPage = () => {
  return (
    <main>
      <Helmet>
        <title>Website Accessibility Statement | ComfortMaster Heating &amp; Air</title>
        <meta name="description" content="ComfortMaster Heating &amp; Air is committed to ensuring digital accessibility for people with disabilities. Learn about our WCAG 2.1 Level AA compliance efforts." />
        <link rel="canonical" href="https://comfortmasterheating.com/website-accessibility" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Accessibility className="w-8 h-8 text-emerald-400" />
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Accessibility</span>
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">
            Website Accessibility Statement
          </h1>
          <p className="text-xl text-stone-300">
            Last Updated: February 27, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Our Commitment to Accessibility</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Accessibility Standards</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Accessibility Technology &amp; Tools</h2>
            <p className="text-slate-700 mb-4 leading-relaxed">
              To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Screen reader enhancements</li>
              <li>Keyboard navigation improvements</li>
              <li>Color contrast adjustments</li>
              <li>Text size and spacing controls</li>
              <li>Focus indicators</li>
              <li>Additional accessibility interface options</li>
            </ul>
            <p className="text-slate-700 mb-6 leading-relaxed">
              This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Ongoing Improvement</h2>
            <p className="text-slate-700 mb-4 leading-relaxed">
              Accessibility is an ongoing initiative. We are:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We understand accessibility is a continuous journey and are committed to making improvements over time.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Third-Party Content</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Feedback &amp; Assistance</h2>
            <p className="text-slate-700 mb-4 leading-relaxed">
              If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us.
            </p>
            <p className="text-slate-700 mb-4 leading-relaxed">
              Please include:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>The page URL</li>
              <li>Description of the issue</li>
              <li>Your preferred contact method</li>
            </ul>
            
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg my-8">
              <p className="text-emerald-800 font-medium mb-4">Contact Us:</p>
              <div className="flex flex-col gap-3">
                <a href="tel:8593576803" className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800">
                  <Phone size={18} />
                  (859) 357-6803
                </a>
                <Link to="/contact" className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800">
                  <Mail size={18} />
                  Contact Us Page
                </Link>
              </div>
            </div>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Good Faith Efforts</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">Terms &amp; Conditions - Accessibility Clause</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
            </p>

          </div>

          <div className="mt-12 pt-8 border-t border-stone-200">
            <Link to="/" className="text-emerald-600 font-semibold hover:text-emerald-700">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default WebsiteAccessibilityPage;
